import React from 'react';
import { useFormikContext } from 'formik';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { AppointmentType } from '@prisma/client';
import { AcceptRequestFlowValues } from '../../../types/appointments';
import {
  waiverOptions,
  waiverTooltip,
} from '../../../utils/form/appointmentForm';
import { isWaiverSelectable } from '../../../helpers/appointments';
import { InputRowType, LoadedUser } from '../../../interfaces';
import InputGroup from '../InputGroup';
import CollapsibleSection from './CollapsibleSection';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import useUser from '../../../hooks/global/useUser';

function MoreOptions() {
  const { values, setFieldValue } = useFormikContext<AcceptRequestFlowValues>();
  const { user: loadedUser } = useUser<LoadedUser>();

  const isConsultation = values.requestKind === 'CONSULTATION';
  const selectedBusinessId = values.businessId || loadedUser?.business.id;

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  // Consultations never show the waiver choice — don't fetch for them
  const { data: waivers, isFetching: isFetchingWaivers } = useGetWaivers({
    enabled: !isConsultation,
    businessId: selectedBusinessId,
  });
  const showWaiverSelect = isWaiverSelectable({
    waivers,
    hasWaiversFeature,
    appointmentType: values.requestKind as AppointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
  });

  // requestWaiver only carries a value while the choice is on screen — the
  // payload must stay silent when the business was never asked
  React.useEffect(() => {
    if (!showWaiverSelect && values.requestWaiver !== undefined) {
      setFieldValue('requestWaiver', undefined);
    }
    if (showWaiverSelect && values.requestWaiver === undefined) {
      setFieldValue('requestWaiver', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWaiverSelect]);

  const inputRows = React.useMemo(
    () =>
      [
        [
          {
            name: 'customDesignMessage',
            label: 'Reply Message',
            type: 'textarea',
          },
        ],
        !isConsultation &&
          showWaiverSelect && [
            {
              name: 'requestWaiver',
              label: 'Waiver',
              type: 'select',
              options: waiverOptions,
              placeholder: 'Select waiver option',
              disabled: isFetchingWaivers,
              tooltip: waiverTooltip,
            },
          ],
        [
          {
            name: 'internalNotes',
            label: 'Internal Notes',
            type: 'textarea',
            placeholder:
              'Optional. These notes are just for you and your team, and can not be viewed by the client.',
          },
        ],
      ].filter(Boolean) as InputRowType[],
    [isConsultation, isFetchingWaivers, showWaiverSelect],
  );

  const chips = React.useMemo(() => {
    const active: string[] = [];
    if (values.customDesignMessage?.trim()) active.push('Reply message');
    if (values.requestWaiver === false) active.push('No waiver');
    if (values.internalNotes?.trim()) active.push('Internal notes');
    return active;
  }, [values.customDesignMessage, values.internalNotes, values.requestWaiver]);

  return (
    <CollapsibleSection
      title="More options"
      subtitle="Reply message, waiver & internal notes"
      icon={<TuneRoundedIcon sx={{ color: 'secondary.main' }} />}
      chips={chips}>
      <InputGroup name="" rows={inputRows} />
    </CollapsibleSection>
  );
}

export default MoreOptions;
